import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { toGoogleMapsPlace, toGoogleMapsDirections, toFlights, toAirbnb, toExpediaHotels, toExpediaVacationRentals, toExpediaCarRental, toUber, toLyft, toParking } from '../utils/helpers';
import { supabase, recordEventView, getSessionId } from '../lib/supabase';
import { useEvent } from '../hooks/useEvents';
import { useAuth } from '../contexts/AuthContext';
import WeatherWidget from './WeatherWidget';
import { isNativePlatform } from '../native/platform';
import { shareContent } from '../native/share';
import { StatusBadge, CountdownBadge, PrimaryMobileButton, SecondaryMobileButton, MobileLoadingSkeleton, MobileSectionHeader } from './mobile';

const EventDetail = ({ savedEvents, onSaveToggle }) => {
  const { id } = useParams();
  const { event, loading, error } = useEvent(id);
  const { user } = useAuth();
  const navigate = useNavigate();
  const [alreadyRegistered, setAlreadyRegistered] = useState(false);
  const [checkingRegistration, setCheckingRegistration] = useState(true);

  // Check if user is already registered for this event
  useEffect(() => {
    const checkRegistration = async () => {
      if (!event?.id || !user?.email) {
        setCheckingRegistration(false);
        return;
      }
      try {
        const { data } = await supabase
          .from('event_registrations')
          .select('id, status')
          .eq('event_id', event.id)
          .eq('attendee_email', user.email.toLowerCase())
          .neq('status', 'cancelled')
          .maybeSingle();
        setAlreadyRegistered(!!data);
      } catch (err) {
        console.debug('Registration check error:', err.message);
      } finally {
        setCheckingRegistration(false);
      }
    };
    checkRegistration();
  }, [event?.id, user?.email]);

  const handleRegisterClick = () => {
    if (!user) {
      navigate(`/login?redirect=/register?event=${event.id}`);
      return;
    }
    navigate(`/register?event=${event.id}`);
  };

  // Track event view and update meta tags
  useEffect(() => {
    if (event?.id) {
      const sessionId = getSessionId();
      recordEventView(event.id, sessionId).catch(err => {
        // Silently fail - analytics function may not exist yet
        console.debug('Event view tracking:', err.message);
      });

      // Update meta tags for Facebook sharing
      const locationStr = event.city || event.venue || '';
      const fullLocation = locationStr + (event.country && event.country !== 'United States' ? `, ${event.country}` : '');
      document.title = `${event.event} - ${fullLocation} | ACP Conference Planner`;
      
      // Update or create meta tags
      const ogImageUrl = `https://confplanners.com/.netlify/functions/og-image?id=${event.id}`;
      const metaTags = [
        { property: 'og:title', content: `${event.event} - ${fullLocation}` },
        { property: 'og:description', content: event.description || `Join us for ${event.event} in ${fullLocation} on ${new Date(event.start).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}` },
        { property: 'og:image', content: ogImageUrl },
        { property: 'og:image:width', content: '1200' },
        { property: 'og:image:height', content: '630' },
        { property: 'og:url', content: window.location.href },
        { property: 'og:type', content: 'event' },
        { property: 'og:site_name', content: 'ACP Conference Planner' },
        { name: 'twitter:card', content: 'summary_large_image' },
        { name: 'twitter:title', content: `${event.event} - ${fullLocation}` },
        { name: 'twitter:description', content: event.description || `Join us for ${event.event} in ${fullLocation}` },
        { name: 'twitter:image', content: ogImageUrl }
      ];

      metaTags.forEach(tag => {
        let meta;
        if (tag.property) {
          meta = document.querySelector(`meta[property="${tag.property}"]`) || document.createElement('meta');
          meta.setAttribute('property', tag.property);
        } else {
          meta = document.querySelector(`meta[name="${tag.name}"]`) || document.createElement('meta');
          meta.setAttribute('name', tag.name);
        }
        meta.setAttribute('content', tag.content);
        if (!document.head.contains(meta)) {
          document.head.appendChild(meta);
        }
      });

      // Cleanup on unmount
      return () => {
        document.title = 'ACP Conference Planner';
      };
    }
  }, [event?.id, event?.event, event?.city, event?.description, event?.image_url, event?.image, event?.start]);

  if (loading) {
    if (isNativePlatform()) {
      return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
          <div className="pt-4">
            <MobileLoadingSkeleton variant="hero" />
            <div className="mt-4 space-y-3 px-4">
              <MobileLoadingSkeleton variant="card" count={2} />
            </div>
          </div>
        </div>
      );
    }
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 text-center">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mx-auto mb-4"></div>
          <div className="h-4 bg-gray-200 rounded w-1/4 mx-auto"></div>
        </div>
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 text-center">
        <h1 className="text-2xl font-bold text-gray-900">Event not found</h1>
        <p className="text-gray-600 mt-2">{error || 'The event you are looking for does not exist.'}</p>
        <Link to="/" className="mt-4 inline-block text-blue-600 hover:underline">
          ← Back to all events
        </Link>
      </div>
    );
  }

  if (isNativePlatform()) {
    return <MobileEventDetail event={event} savedEvents={savedEvents} onSaveToggle={onSaveToggle} alreadyRegistered={alreadyRegistered} checkingRegistration={checkingRegistration} onRegisterClick={handleRegisterClick} />;
  }

  const isSaved = savedEvents.includes(event.id);
  
  // Parse date string as local time to avoid timezone shift
  const parseLocalDate = (dateStr) => {
    if (typeof dateStr === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      const [year, month, day] = dateStr.split('-').map(Number);
      return new Date(year, month - 1, day);
    }
    return new Date(dateStr);
  };
  
  const startDate = event.start ? parseLocalDate(event.start) : null;
  const endDate = event.end ? parseLocalDate(event.end) : null;
  const days = (startDate && endDate) ? Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1 : 1;

  const safeStart = event.start || '';
  const safeEnd = event.end || '';
  const safeEventName = event.event || event.title || 'Event';

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <Link to="/" className="inline-flex items-center text-gray-600 hover:text-gray-900">
          <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back to all events
        </Link>
      </div>

      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        {/* Event Header */}
        <div className="relative h-64 bg-gray-200 overflow-hidden">
          <img 
            src={event.image_url || event.image || 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80'}
            alt={event.event}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent">
            <div className="absolute bottom-0 left-0 p-6 text-white">
              <h1 className="text-3xl font-bold mb-2">{event.event}</h1>
              <p className="text-lg">{event.organization}{event.venue ? ` • ${event.venue}` : ''}{event.city ? `, ${event.city}` : ''}</p>
            </div>
          </div>
          <button
            onClick={() => onSaveToggle(event.id)}
            className="absolute top-4 right-4 p-2 rounded-full bg-white/80 hover:bg-white transition-colors"
            aria-label={isSaved ? 'Remove from saved' : 'Save event'}
          >
            <svg
              className={`w-6 h-6 ${isSaved ? 'text-yellow-400 fill-current' : 'text-gray-400 hover:text-yellow-400'}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
              />
            </svg>
          </button>
        </div>

        <div className="p-6 md:p-8">
          {/* Event Details */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="md:col-span-2">
              <div className="prose max-w-none">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Event Details</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  <div>
                    <h3 className="text-sm font-medium text-gray-500">Date</h3>
                    <p className="mt-1 text-gray-900">
                      {startDate ? startDate.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }) : 'Date TBA'}
                      {days > 1 && ` (${days} days)`}
                    </p>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-gray-500">Event Type</h3>
                    <p className="mt-1">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        event.event_type === 'virtual' ? 'bg-blue-100 text-blue-800' :
                        event.event_type === 'hybrid' ? 'bg-purple-100 text-purple-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {event.event_type === 'virtual' ? '🖥️ Virtual' :
                         event.event_type === 'hybrid' ? '🏢 Hybrid (In-Person + Virtual)' :
                         '🏢 In-Person'}
                      </span>
                    </p>
                  </div>
                  {event.event_type !== 'virtual' && (
                    <>
                      <div>
                        <h3 className="text-sm font-medium text-gray-500">Location</h3>
                        {event.venue && <p className="mt-1 text-gray-900">{event.venue}</p>}
                        <p className="text-gray-600">{event.address && event.address !== event.city ? event.address : event.city}{event.country && event.country !== 'United States' ? `, ${event.country}` : ''}</p>
                      </div>
                      <div>
                        <h3 className="text-sm font-medium text-gray-500">Airport</h3>
                        <p className="mt-1 text-gray-900">{event.airport || 'Not specified'}</p>
                      </div>
                    </>
                  )}
                  <div>
                    <h3 className="text-sm font-medium text-gray-500">Status</h3>
                    <p className="mt-1">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        event.status === 'cancelled' 
                          ? 'bg-red-500 text-white' 
                          : event.status === 'ongoing'
                          ? 'bg-blue-500 text-white'
                          : event.status === 'past'
                          ? 'bg-gray-500 text-white'
                          : event.status === 'confirmed' || event.status === 'upcoming'
                          ? 'bg-green-100 text-green-800' 
                          : event.status === 'pending'
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {event.status === 'cancelled' ? 'Cancelled' 
                          : event.status === 'ongoing' ? 'Happening Now'
                          : event.status === 'past' ? 'Past Event'
                          : event.status === 'pending' ? 'Pending'
                          : event.status === 'confirmed' ? 'Confirmed' 
                          : event.status === 'upcoming' ? 'Upcoming'
                          : event.status === 'draft' ? 'Draft'
                          : 'Tentative'}
                      </span>
                    </p>
                  </div>
                </div>

                {/* Virtual Event Details */}
                {(event.event_type === 'virtual' || event.event_type === 'hybrid') && (
                  <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                    <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-100 mb-3 flex items-center">
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      Virtual Meeting Information
                    </h3>
                    <div className="space-y-3">
                      {event.virtual_platform && (
                        <div>
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Platform: </span>
                          <span className="text-sm text-gray-900 dark:text-white">{event.virtual_platform}</span>
                        </div>
                      )}
                      {event.virtual_url && (
                        <div>
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">Join URL:</span>
                          <div className="flex items-center gap-2 bg-white dark:bg-gray-800 p-2 rounded border border-gray-200 dark:border-gray-700">
                            <a href={event.virtual_url} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline flex-1 truncate">
                              {event.virtual_url.replace(/^https?:\/\//, '').split('?')[0]}
                            </a>
                            <button
                              onClick={(e) => {
                                navigator.clipboard.writeText(event.virtual_url);
                                const btn = e.currentTarget;
                                const originalContent = btn.textContent;
                                btn.textContent = '✓ Copied!';
                                setTimeout(() => btn.textContent = originalContent, 2000);
                              }}
                              className="px-3 py-1 text-xs bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded transition-colors whitespace-nowrap"
                              title="Copy full URL"
                            >
                              📋 Copy
                            </button>
                          </div>
                        </div>
                      )}
                      {event.virtual_meeting_id && (
                        <div>
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Meeting ID: </span>
                          <span className="text-sm text-gray-900 dark:text-white font-mono">{event.virtual_meeting_id}</span>
                        </div>
                      )}
                      {event.virtual_passcode && (
                        <div>
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Passcode: </span>
                          <span className="text-sm text-gray-900 dark:text-white font-mono">{event.virtual_passcode}</span>
                        </div>
                      )}
                      {event.virtual_url && (
                        <div className="mt-4">
                          <a
                            href={event.virtual_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors"
                          >
                            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                            Join Virtual Event
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Event Description */}
                {event.description && (
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">About This Event</h3>
                    <div className="prose prose-sm max-w-none">
                      <FormattedDescription text={event.description} />
                    </div>
                  </div>
                )}

                {(event.website_url || event.registration_url) && (
                  <div className="mb-6 flex flex-wrap gap-3">
                    {event.website_url && (
                      <a
                        href={event.website_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-800 font-medium rounded-lg transition-colors"
                      >
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                        </svg>
                        Event Website
                      </a>
                    )}
                    {event.registration_url && (
                      <a
                        href={event.registration_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-6 py-3 bg-primary hover:bg-primary-dark text-white font-medium rounded-lg transition-colors"
                      >
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
                        </svg>
                        Register for Event
                      </a>
                    )}
                  </div>
                )}

                {/* Share Event */}
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Share This Event</h3>
                  <div className="flex flex-wrap gap-3">
                    <button
                      onClick={() => {
                        const shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}&quote=${encodeURIComponent(`Join me at ${safeEventName} in ${event.city}! ${startDate ? startDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : ''}`)}`;
                        window.open(shareUrl, '_blank', 'width=600,height=400');
                      }}
                      className="inline-flex items-center px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors"
                    >
                      <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                      </svg>
                      Share on Facebook
                    </button>
                    <button
                      onClick={() => {
                        if (navigator.share) {
                          navigator.share({
                            title: safeEventName,
                            text: `Join me at ${safeEventName} in ${event.city}! ${startDate ? startDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : ''}`,
                            url: window.location.href
                          });
                        } else {
                          navigator.clipboard.writeText(window.location.href);
                          alert('Event link copied to clipboard!');
                        }
                      }}
                      className="inline-flex items-center px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-800 font-medium rounded-lg transition-colors"
                    >
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m9.032 4.026a9.001 9.001 0 01-7.432 0m9.032-4.026A9.001 9.001 0 0112 3c-4.474 0-8.268 3.12-9.032 7.326m0 0A9.001 9.001 0 0012 21c4.474 0 8.268-3.12 9.032-7.326" />
                      </svg>
                      Share Event
                    </button>
                    <button
                      onClick={async () => {
                        try {
                          const cardUrl = `https://confplanners.com/.netlify/functions/og-image?id=${event.id}`;
                          const res = await fetch(cardUrl);
                          const blob = await res.blob();
                          const url = URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = `${safeEventName.replace(/[^a-zA-Z0-9]/g, '_')}_share-card.png`;
                          document.body.appendChild(a);
                          a.click();
                          document.body.removeChild(a);
                          URL.revokeObjectURL(url);
                        } catch (e) {
                          alert('Could not download share card. Please try again.');
                        }
                      }}
                      className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-medium rounded-lg transition-colors"
                    >
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                      Download Share Card
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">Download the share card image to post on Instagram Stories or feed.</p>
                </div>

                {event.notes && (
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Schedule & Notes</h3>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <pre className="text-sm text-gray-700 whitespace-pre-wrap font-sans leading-relaxed">{event.notes}</pre>
                    </div>
                  </div>
                )}

              </div>

              {/* Travel & Accommodation Section - Only show for in-person and hybrid events */}
              {event.event_type !== 'virtual' && (
              <div className="mt-12">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Travel & Accommodation</h2>
                
                <div className="space-y-8">
                  {/* Transportation */}
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-3">Getting There</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      <a
                        href={toFlights({
                          to: event.airport || event.city || event.location,
                          start: event.start,
                          end: event.end
                        })}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex flex-col items-center justify-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-center"
                      >
                        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mb-2">
                          <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                          </svg>
                        </div>
                        <span className="text-sm font-medium text-gray-900">Book Flights</span>
                        <span className="text-xs text-gray-500">Expedia</span>
                      </a>
                      
                      <a
                        href={toGoogleMapsDirections(event.address ? event.address : event.venue && event.city ? `${event.venue}, ${event.city}` : '')}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex flex-col items-center justify-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-center"
                        onClick={(e) => {
                          // Fallback in case the link doesn't open in a new tab
                          if (window.getSelection().toString() === '' && !e.ctrlKey && !e.metaKey) {
                            e.preventDefault();
                            window.open(
                              toGoogleMapsDirections(event.address ? event.address : event.venue && event.city ? `${event.venue}, ${event.city}` : ''),
                              '_blank',
                              'noopener,noreferrer'
                            );
                          }
                        }}
                      >
                        <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center mb-2">
                          <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                        </div>
                        <span className="text-sm font-medium text-gray-900">Get Directions</span>
                        <span className="text-xs text-gray-500">Google Maps</span>
                      </a>
                      
                      <a
                        href={toUber(event.address || event.location || event.venue_name || event.venue || 'Event Venue', event.latitude, event.longitude)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex flex-col items-center justify-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-center"
                      >
                        <div className="w-10 h-10 bg-black rounded-full flex items-center justify-center mb-2">
                          <span className="text-white text-xs font-bold">UBER</span>
                        </div>
                        <span className="text-sm font-medium text-gray-900">Ride to Venue</span>
                        <span className="text-xs text-gray-500">Destination pre-filled</span>
                      </a>
                    </div>
                  </div>

                  {/* Hotels */}
                  <div>
                    <div className="flex justify-between items-center mb-3">
                      <h3 className="text-lg font-medium text-gray-900">Book Your Stay</h3>
                      <div className="flex space-x-2">
                        <a
                          href={toExpediaHotels(event.location || event.address || (event.venue_name ? `${event.venue_name}, ${event.city}` : event.city), event.start, event.end)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-blue-600 hover:underline"
                        >
                          Hotels
                        </a>
                        <span className="text-gray-300">|</span>
                        <a
                          href={toExpediaVacationRentals(event.location || event.address || event.city, event.start, event.end)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-blue-600 hover:underline"
                        >
                          Vacation Rentals
                        </a>
                        <span className="text-gray-300">|</span>
                        <a
                          href={toAirbnb(event.location || event.address || event.city, event.start, event.end)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-blue-600 hover:underline"
                        >
                          Airbnb
                        </a>
                      </div>
                    </div>
                    
                    <div className="space-y-4">
                      {event.hotels && event.hotels.length > 0 ? (
                        event.hotels.map((hotel, index) => (
                          <div key={index} className="border border-gray-200 rounded-lg overflow-hidden">
                            <div className="p-4">
                              <div className="flex justify-between items-start">
                                <div>
                                  <h4 className="font-medium text-gray-900">{hotel.name}</h4>
                                  <p className="text-sm text-gray-600">{hotel.distance} • {hotel.note}</p>
                                </div>
                                <a
                                  href={toExpediaHotels(hotel.name || event.city, event.start, event.end)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-sm text-blue-600 hover:underline whitespace-nowrap ml-4"
                                >
                                  Book on Expedia
                                </a>
                              </div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <p className="text-sm text-gray-500">Search for hotels near {event.city || 'the venue'} using the links above.</p>
                      )}
                    </div>
                  </div>

                  {/* Local Transportation */}
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-3">Local Transportation</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <a
                        href={toUber(event.address || event.location || event.venue_name || event.venue || 'Event Venue', event.latitude, event.longitude)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        <div className="w-10 h-10 bg-black rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                          <span className="text-white text-xs font-bold">UBER</span>
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">Uber to Venue</div>
                          <div className="text-xs text-gray-500">Destination pre-filled</div>
                        </div>
                      </a>
                      <a
                        href={toLyft(event.address || event.location || event.venue_name || event.venue || 'Event Venue', event.latitude, event.longitude)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        <div className="w-10 h-10 bg-pink-500 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                          <span className="text-white text-xs font-bold">Lyft</span>
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">Lyft to Venue</div>
                          <div className="text-xs text-gray-500">Destination pre-filled</div>
                        </div>
                      </a>
                      <a
                        href={toParking(event.address || event.location || event.venue_name)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                          <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                          </svg>
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">Find Parking</div>
                          <div className="text-xs text-gray-500">Reserve a spot</div>
                        </div>
                      </a>
                      {event.airport && (
                        <a
                          href={`https://www.google.com/search?q=${encodeURIComponent(`${event.airport} airport transportation`)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                        >
                          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                            <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                              <path d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                            </svg>
                          </div>
                          <div>
                            <div className="font-medium text-gray-900">Airport Shuttle</div>
                            <div className="text-xs text-gray-500">Shuttle services</div>
                          </div>
                        </a>
                      )}
                      <a
                        href={toExpediaCarRental(event.address || event.location || (event.venue && event.city ? `${event.venue}, ${event.city}` : event.city), event.start, event.end)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                          <svg className="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h8m-8 5h8m-4-9v2m-4 14h8a2 2 0 002-2v-3a2 2 0 00-2-2H8a2 2 0 00-2 2v3a2 2 0 002 2zM7 10a2 2 0 11-4 0 2 2 0 014 0zm14 0a2 2 0 11-4 0 2 2 0 014 0z" />
                          </svg>
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">Car Rental</div>
                          <div className="text-xs text-gray-500">Rent in {event.city || 'this area'}</div>
                        </div>
                      </a>
                    </div>
                  </div>
                </div>
              </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Registration CTA */}
              <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                <div className="p-4">
                  <h3 className="text-lg font-medium text-gray-900 mb-3">Registration</h3>
                  {checkingRegistration ? (
                    <div className="flex items-center justify-center py-3">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                    </div>
                  ) : alreadyRegistered ? (
                    <div className="space-y-3">
                      <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                        <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span className="text-sm font-medium text-green-800">You're registered!</span>
                      </div>
                      <Link to="/my-tickets" className="block w-full text-center px-4 py-2.5 bg-primary hover:bg-primary-dark text-white font-medium rounded-lg transition-colors">
                        View My Tickets
                      </Link>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {event.is_paid_event && event.registration_fee > 0 && (
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-600">Registration Fee:</span>
                          <span className="font-semibold text-gray-900">${event.registration_fee.toFixed(2)}</span>
                        </div>
                      )}
                      <button
                        onClick={handleRegisterClick}
                        className="w-full px-4 py-2.5 bg-primary hover:bg-primary-dark text-white font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
                        </svg>
                        Register Now
                      </button>
                      {!user && (
                        <p className="text-xs text-gray-500 text-center">You'll need to log in to register</p>
                      )}
                    </div>
                  )}
                  {event.registration_url && (
                    <a
                      href={event.registration_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-2 block text-center text-sm text-primary hover:underline"
                    >
                      Or register on organizer's site →
                    </a>
                  )}
                </div>
              </div>

              {/* Map - Only show for in-person and hybrid events */}
              {event.event_type !== 'virtual' && (
              <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                <div className="p-4 border-b border-gray-200">
                  <h3 className="text-lg font-medium text-gray-900">Location</h3>
                </div>
                <div className="h-64 bg-gray-100">
                  <iframe
                    title="Event Location"
                    width="100%"
                    height="100%"
                    frameBorder="0"
                    scrolling="no"
                    marginHeight="0"
                    marginWidth="0"
                    src={`https://maps.google.com/maps?q=${encodeURIComponent(event.address || `${event.venue}, ${event.city}`)}&t=m&z=15&output=embed`}
                    className="border-0"
                  ></iframe>
                </div>
                <div className="p-4">
                  <p className="text-sm text-gray-700 mb-3">{event.venue}</p>
                  <p className="text-sm text-gray-500">{event.address || event.city}</p>
                  <div className="mt-3 flex space-x-2">
                    <a
                      href={toGoogleMapsPlace(event.address || `${event.venue}, ${event.city}`)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                    >
                      <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      View Map
                    </a>
                    <a
                      href={toGoogleMapsDirections(event.address || `${event.venue}, ${event.city}`)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                    >
                      <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Directions
                    </a>
                  </div>
                </div>
              </div>
              )}

              {/* Weather Widget - use city, or extract from location/venue_name as fallback - Only show for in-person and hybrid events */}
              {event.event_type !== 'virtual' && (
              <WeatherWidget 
                city={event.city || event.location?.split(',').slice(-2).join(',').trim() || event.venue_name?.split(',').slice(-2).join(',').trim()} 
                eventDate={event.start} 
              />
              )}

              {/* Save to Calendar */}
              <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                <div className="p-4">
                  <h3 className="text-lg font-medium text-gray-900 mb-3">Add to Calendar</h3>
                  <div className="space-y-2">
                    <a
                      href={`https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(safeEventName)}&dates=${safeStart.replace(/-/g, '')}/${safeEnd.replace(/-/g, '')}&details=${encodeURIComponent(`Location: ${event.venue}, ${event.city}\n\n${event.notes || ''}`)}&location=${encodeURIComponent(event.address || `${event.venue}, ${event.city}`)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      Google Calendar
                    </a>
                    <a
                      href={`https://outlook.office.com/calendar/0/action/compose?subject=${encodeURIComponent(safeEventName)}&startdt=${safeStart}&enddt=${safeEnd}&body=${encodeURIComponent(`Location: ${event.venue}, ${event.city}\n\n${event.notes || ''}`)}&location=${encodeURIComponent(event.address || `${event.venue}, ${event.city}`)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      Outlook
                    </a>
                    <a
                      href={`data:text/calendar;charset=utf8,${encodeURIComponent(
                        `BEGIN:VCALENDAR\nVERSION:2.0\nBEGIN:VEVENT\nDTSTART:${safeStart.replace(/-/g, '')}\nDTEND:${safeEnd.replace(/-/g, '')}\nSUMMARY:${safeEventName}\nDESCRIPTION:${event.notes || ''}\n\nLocation: ${event.venue}, ${event.city}\nLOCATION:${event.address || `${event.venue}, ${event.city}`}\nEND:VEVENT\nEND:VCALENDAR`
                      )}`}
                      download={`${safeEventName.replace(/\s+/g, '_')}.ics`}
                      className="flex items-center justify-center w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                      Download .ics
                    </a>
                  </div>
                </div>
              </div>

              {/* Share Event */}
              <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                <div className="p-4">
                  <h3 className="text-lg font-medium text-gray-900 mb-3">Share Event</h3>
                  <div className="flex flex-wrap gap-2">
                    {/* Facebook */}
                    <a
                      href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 flex items-center justify-center bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                      title="Share on Facebook"
                    >
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M18.77 7.46H14.5v-1.9c0-.9.6-1.1 1-1.1h3V.5h-4.33C10.24.5 9.5 3.44 9.5 5.32v2.15h-3v4h3v12h5v-12h3.85l.42-4z"/>
                      </svg>
                    </a>

                    {/* X (Twitter) */}
                    <a
                      href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(window.location.href)}&text=${encodeURIComponent(`Check out ${safeEventName}!`)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 flex items-center justify-center bg-black hover:bg-gray-800 text-white rounded-lg transition-colors"
                      title="Share on X"
                    >
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                      </svg>
                    </a>

                    {/* LinkedIn */}
                    <a
                      href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 flex items-center justify-center bg-blue-700 hover:bg-blue-800 text-white rounded-lg transition-colors"
                      title="Share on LinkedIn"
                    >
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                      </svg>
                    </a>

                    {/* WhatsApp */}
                    <a
                      href={`https://wa.me/?text=${encodeURIComponent(`Check out ${safeEventName}! ${window.location.href}`)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 flex items-center justify-center bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors"
                      title="Share on WhatsApp"
                    >
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                      </svg>
                    </a>

                    {/* Email */}
                    <a
                      href={`mailto:?subject=${encodeURIComponent(safeEventName)}&body=${encodeURIComponent(`Check out this event: ${safeEventName}\n\nDate: ${startDate ? startDate.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }) : 'TBA'}\nLocation: ${event.venue}, ${event.city}\n\n${window.location.href}`)}`}
                      className="w-10 h-10 flex items-center justify-center bg-gray-500 hover:bg-gray-600 text-white rounded-lg transition-colors"
                      title="Share via Email"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </a>

                    {/* Copy Link */}
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(window.location.href);
                        alert('Link copied to clipboard!');
                      }}
                      className="w-10 h-10 flex items-center justify-center bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg transition-colors"
                      title="Copy Link"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                    </button>

                    {/* Download Share Card */}
                    <button
                      onClick={async () => {
                        try {
                          const cardUrl = `https://confplanners.com/.netlify/functions/og-image?id=${event.id}`;
                          const res = await fetch(cardUrl);
                          const blob = await res.blob();
                          const url = URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = `${safeEventName.replace(/[^a-zA-Z0-9]/g, '_')}_share-card.png`;
                          document.body.appendChild(a);
                          a.click();
                          document.body.removeChild(a);
                          URL.revokeObjectURL(url);
                        } catch (e) {
                          alert('Could not download share card. Please try again.');
                        }
                      }}
                      className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white rounded-lg transition-colors"
                      title="Download Share Card (for Instagram)"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">Download the share card to post on Instagram.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Mobile Event Detail ─────────────────────────────────────
const MobileEventDetail = ({ event, savedEvents, onSaveToggle, alreadyRegistered, checkingRegistration, onRegisterClick }) => {
  const navigate = useNavigate();
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  const parseLocalDate = (dateStr) => {
    if (typeof dateStr === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      const [y, m, d] = dateStr.split('-').map(Number);
      return new Date(y, m - 1, d);
    }
    return new Date(dateStr);
  };

  const startDate = parseLocalDate(event.start);
  const endDate = parseLocalDate(event.end);
  const days = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;
  const isSaved = savedEvents.includes(event.id);
  const imageUrl = event.image_url || event.image;

  const formatShort = (d) =>
    d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  const handleShare = async () => {
    await shareContent({
      title: event.event,
      text: `Join me at ${event.event} in ${event.city}! ${formatShort(startDate)}`,
      url: window.location.href,
      dialogTitle: 'Share Event',
    });
  };

  const travelLinks = [
    { label: 'Flights', href: toFlights({ to: event.airport || event.city, start: event.start, end: event.end }), icon: 'M12 19l9 2-9-18-9 18 9-2zm0 0v-8', color: 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400' },
    { label: 'Directions', href: toGoogleMapsDirections(event.address || `${event.venue}, ${event.city}`), icon: 'M17.657 16.657L13.414 20.9a2 2 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z M15 11a3 3 0 11-6 0 3 3 0 016 0z', color: 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400' },
    { label: 'Hotels', href: toExpediaHotels(event.location || event.address || event.city, event.start, event.end), icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6', color: 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400' },
    { label: 'Uber', href: toUber(event.address || event.venue || 'Event Venue', event.latitude, event.longitude), icon: '', color: 'bg-gray-800 text-white', badge: 'UBER' },
    { label: 'Lyft', href: toLyft(event.address || event.venue || 'Event Venue', event.latitude, event.longitude), icon: '', color: 'bg-pink-500 text-white', badge: 'Lyft' },
    { label: 'Parking', href: toParking(event.address || event.venue), icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z', color: 'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400' },
    { label: 'Airbnb', href: toAirbnb(event.location || event.address || event.city, event.start, event.end), icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6', color: 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400' },
    { label: 'Car Rental', href: toExpediaCarRental(event.address || event.city, event.start, event.end), icon: 'M8 7h8m-8 5h8m-4-9v2m-4 14h8a2 2 0 002-2v-3a2 2 0 00-2-2H8a2 2 0 00-2 2v3a2 2 0 002 2z', color: 'bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400' },
  ];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 pb-20">
      {/* Hero image with back button */}
      <div className="relative w-full h-64 bg-gray-200 dark:bg-gray-800 overflow-hidden">
        {!imageLoaded && !imageError && (
          <div className="absolute inset-0 animate-pulse bg-gray-200 dark:bg-gray-700" />
        )}
        {imageError ? (
          <div className="flex items-center justify-center w-full h-full bg-gradient-to-br from-primary to-primary-dark">
            <span className="text-white/50 text-4xl font-display font-bold">ACP</span>
          </div>
        ) : (
          <img
            src={imageUrl}
            alt={event.event}
            className={`w-full h-full object-cover transition-opacity duration-500 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
            onLoad={() => setImageLoaded(true)}
            onError={() => setImageError(true)}
          />
        )}
        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />

        {/* Back button */}
        <button
          onClick={() => navigate(-1)}
          className="absolute top-4 left-4 flex items-center justify-center w-10 h-10 rounded-full bg-white/20 backdrop-blur-md active:scale-95 transition-transform"
          style={{ marginTop: 'env(safe-area-inset-top)' }}
          aria-label="Go back"
        >
          <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>

        {/* Save & Share buttons */}
        <div className="absolute top-4 right-4 flex gap-2" style={{ marginTop: 'env(safe-area-inset-top)' }}>
          <button
            onClick={() => onSaveToggle(event.id)}
            className="flex items-center justify-center w-10 h-10 rounded-full bg-white/20 backdrop-blur-md active:scale-95 transition-transform"
            aria-label={isSaved ? 'Remove from saved' : 'Save event'}
          >
            <svg
              className={`w-5 h-5 ${isSaved ? 'text-amber-400 fill-current' : 'text-white'}`}
              fill={isSaved ? 'currentColor' : 'none'}
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
            </svg>
          </button>
          <button
            onClick={handleShare}
            className="flex items-center justify-center w-10 h-10 rounded-full bg-white/20 backdrop-blur-md active:scale-95 transition-transform"
            aria-label="Share event"
          >
            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m9.032 4.026a9.001 9.001 0 01-7.432 0m9.032-4.026A9.001 9.001 0 0112 3c-4.474 0-8.268 3.12-9.032 7.326m0 0A9.001 9.001 0 0012 21c4.474 0 8.268-3.12 9.032-7.326" />
            </svg>
          </button>
        </div>

        {/* Title overlay */}
        <div className="absolute bottom-0 left-0 right-0 p-4">
          {event.organization && (
            <span className="inline-block text-xs font-semibold px-2 py-0.5 rounded-full bg-secondary/90 text-white mb-2">
              {event.organization}
            </span>
          )}
          <h1 className="font-display text-xl font-bold text-white leading-tight mb-1">
            {event.event}
          </h1>
          <div className="flex items-center gap-2 flex-wrap">
            <StatusBadge status={event.status} size="sm" />
            {event.status !== 'past' && event.status !== 'cancelled' && (
              <CountdownBadge targetDate={event.start} size="sm" />
            )}
          </div>
        </div>
      </div>

      {/* Info pills */}
      <div className="px-4 mt-4">
        <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-1">
          {/* Date pill */}
          <div className="flex items-center gap-2 px-3 py-2 bg-white dark:bg-gray-800 rounded-xl shadow-card shrink-0">
            <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <div className="text-xs">
              <p className="font-semibold text-gray-900 dark:text-white">{formatShort(startDate)}</p>
              {days > 1 && <p className="text-gray-500">{days} days</p>}
            </div>
          </div>
          {/* Location pill */}
          {event.event_type !== 'virtual' && event.venue && (
            <div className="flex items-center gap-2 px-3 py-2 bg-white dark:bg-gray-800 rounded-xl shadow-card shrink-0">
              <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a2 2 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <div className="text-xs">
                <p className="font-semibold text-gray-900 dark:text-white truncate max-w-[120px]">{event.venue}</p>
                <p className="text-gray-500">{event.city}</p>
              </div>
            </div>
          )}
          {/* Type pill */}
          <div className="flex items-center gap-2 px-3 py-2 bg-white dark:bg-gray-800 rounded-xl shadow-card shrink-0">
            <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            <span className="text-xs font-semibold text-gray-900 dark:text-white">
              {event.event_type === 'virtual' ? 'Virtual' : event.event_type === 'hybrid' ? 'Hybrid' : 'In-Person'}
            </span>
          </div>
          {/* Airport pill */}
          {event.airport && event.event_type !== 'virtual' && (
            <div className="flex items-center gap-2 px-3 py-2 bg-white dark:bg-gray-800 rounded-xl shadow-card shrink-0">
              <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
              </svg>
              <span className="text-xs font-semibold text-gray-900 dark:text-white">{event.airport}</span>
            </div>
          )}
        </div>
      </div>

      {/* About section */}
      {event.description && (
        <section className="mt-6 px-4">
          <MobileSectionHeader title="About This Event" />
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-card p-4">
            <div className="prose prose-sm max-w-none dark:prose-invert">
              <FormattedDescription text={event.description} />
            </div>
          </div>
        </section>
      )}

      {/* Virtual info */}
      {(event.event_type === 'virtual' || event.event_type === 'hybrid') && event.virtual_url && (
        <section className="mt-6 px-4">
          <MobileSectionHeader title="Virtual Meeting" />
          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-2xl p-4 border border-blue-200 dark:border-blue-800">
            {event.virtual_platform && (
              <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">
                <span className="font-medium">Platform:</span> {event.virtual_platform}
              </p>
            )}
            {event.virtual_meeting_id && (
              <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">
                <span className="font-medium">Meeting ID:</span> <span className="font-mono">{event.virtual_meeting_id}</span>
              </p>
            )}
            {event.virtual_passcode && (
              <p className="text-sm text-gray-700 dark:text-gray-300 mb-3">
                <span className="font-medium">Passcode:</span> <span className="font-mono">{event.virtual_passcode}</span>
              </p>
            )}
            <a
              href={event.virtual_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-xl active:scale-95 transition-transform"
            >
              Join Virtual Event
            </a>
          </div>
        </section>
      )}

      {/* Schedule & Notes */}
      {event.notes && (
        <section className="mt-6 px-4">
          <MobileSectionHeader title="Schedule & Notes" />
          <div className="bg-gray-50 dark:bg-gray-800 rounded-2xl p-4">
            <pre className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap font-sans leading-relaxed">{event.notes}</pre>
          </div>
        </section>
      )}

      {/* Travel section */}
      {event.event_type !== 'virtual' && (
        <section className="mt-6 px-4">
          <MobileSectionHeader title="Travel & Accommodation" />
          <div className="grid grid-cols-4 gap-3">
            {travelLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
                className="flex flex-col items-center justify-center p-2 bg-white dark:bg-gray-800 rounded-2xl shadow-card active:scale-95 transition-transform min-h-[80px]"
              >
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center mb-1 ${link.color}`}>
                  {link.badge ? (
                    <span className="text-[8px] font-bold">{link.badge}</span>
                  ) : (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={link.icon} />
                    </svg>
                  )}
                </div>
                <span className="text-[10px] font-medium text-gray-700 dark:text-gray-300 text-center leading-tight">{link.label}</span>
              </a>
            ))}
          </div>

          {/* Hotels list */}
          {event.hotels && event.hotels.length > 0 && (
            <div className="mt-4 space-y-2">
              {event.hotels.map((hotel, i) => (
                <a
                  key={i}
                  href={toExpediaHotels(hotel.name || event.city, event.start, event.end)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-between bg-white dark:bg-gray-800 rounded-2xl p-3 shadow-card active:scale-[0.98] transition-transform"
                >
                  <div className="min-w-0 flex-1">
                    <h4 className="font-semibold text-sm text-gray-900 dark:text-white truncate">{hotel.name}</h4>
                    <p className="text-xs text-gray-500 truncate">{hotel.distance} • {hotel.note}</p>
                  </div>
                  <svg className="w-4 h-4 text-gray-300 flex-shrink-0 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </a>
              ))}
            </div>
          )}
        </section>
      )}

      {/* Map */}
      {event.event_type !== 'virtual' && (
        <section className="mt-6 px-4">
          <MobileSectionHeader title="Location" />
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-card overflow-hidden">
            <div className="h-48 bg-gray-100 dark:bg-gray-700">
              <iframe
                title="Event Location"
                width="100%"
                height="100%"
                frameBorder="0"
                scrolling="no"
                marginHeight="0"
                marginWidth="0"
                src={`https://maps.google.com/maps?q=${encodeURIComponent(event.address || `${event.venue}, ${event.city}`)}&t=m&z=15&output=embed`}
                className="border-0"
              />
            </div>
            <div className="p-3">
              <p className="text-sm font-medium text-gray-900 dark:text-white">{event.venue}</p>
              <p className="text-xs text-gray-500">{event.address || event.city}</p>
              <div className="flex gap-2 mt-2">
                <a
                  href={toGoogleMapsPlace(event.address || `${event.venue}, ${event.city}`)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1"
                >
                  <SecondaryMobileButton fullWidth size="sm">View Map</SecondaryMobileButton>
                </a>
                <a
                  href={toGoogleMapsDirections(event.address || `${event.venue}, ${event.city}`)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1"
                >
                  <SecondaryMobileButton fullWidth size="sm">Directions</SecondaryMobileButton>
                </a>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Weather */}
      {event.event_type !== 'virtual' && (
        <section className="mt-6 px-4">
          <WeatherWidget city={event.city} eventDate={event.start} />
        </section>
      )}

      {/* Calendar */}
      <section className="mt-6 px-4">
        <MobileSectionHeader title="Add to Calendar" />
        <div className="flex gap-2">
          <a
            href={`https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(event.event)}&dates=${event.start.replace(/-/g, '')}/${event.end.replace(/-/g, '')}&details=${encodeURIComponent(`Location: ${event.venue}, ${event.city}`)}&location=${encodeURIComponent(event.address || `${event.venue}, ${event.city}`)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1"
          >
            <SecondaryMobileButton fullWidth size="sm">Google Calendar</SecondaryMobileButton>
          </a>
          <a
            href={`data:text/calendar;charset=utf8,${encodeURIComponent(`BEGIN:VCALENDAR\nVERSION:2.0\nBEGIN:VEVENT\nDTSTART:${event.start.replace(/-/g, '')}\nDTEND:${event.end.replace(/-/g, '')}\nSUMMARY:${event.event}\nLOCATION:${event.address || `${event.venue}, ${event.city}`}\nEND:VEVENT\nEND:VCALENDAR`)}`}
            download={`${event.event.replace(/\s+/g, '_')}.ics`}
            className="flex-1"
          >
            <SecondaryMobileButton fullWidth size="sm">.ics File</SecondaryMobileButton>
          </a>
        </div>
      </section>

      {/* Sticky bottom registration bar */}
      <div
        className="fixed bottom-0 inset-x-0 z-40 bg-white/95 dark:bg-gray-900/95 backdrop-blur border-t border-gray-200 dark:border-gray-800 px-4 py-3"
        style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 0.75rem)' }}
      >
        <div className="flex items-center gap-3">
          <button
            onClick={() => onSaveToggle(event.id)}
            className="flex items-center justify-center w-11 h-11 rounded-xl bg-gray-100 dark:bg-gray-800 active:scale-95 transition-transform flex-shrink-0"
            aria-label={isSaved ? 'Remove from saved' : 'Save event'}
          >
            <svg
              className={`w-5 h-5 ${isSaved ? 'text-amber-400 fill-current' : 'text-gray-400'}`}
              fill={isSaved ? 'currentColor' : 'none'}
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
            </svg>
          </button>
          <button
            onClick={handleShare}
            className="flex items-center justify-center w-11 h-11 rounded-xl bg-gray-100 dark:bg-gray-800 active:scale-95 transition-transform flex-shrink-0"
            aria-label="Share event"
          >
            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m9.032 4.026a9.001 9.001 0 01-7.432 0m9.032-4.026A9.001 9.001 0 0112 3c-4.474 0-8.268 3.12-9.032 7.326m0 0A9.001 9.001 0 0012 21c4.474 0 8.268-3.12 9.032-7.326" />
            </svg>
          </button>
          {checkingRegistration ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
            </div>
          ) : alreadyRegistered ? (
            <Link to="/my-tickets" className="flex-1">
              <PrimaryMobileButton fullWidth icon={
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              }>
                View My Tickets
              </PrimaryMobileButton>
            </Link>
          ) : (
            <div className="flex-1" onClick={onRegisterClick}>
              <PrimaryMobileButton fullWidth icon={
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
                </svg>
              }>
                Register Now
              </PrimaryMobileButton>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const FormattedDescription = ({ text }) => {
  if (!text) return null;

  const lines = text.split('\n');
  const elements = [];
  let bulletGroup = [];
  let prevWasBullet = false;

  const flushBullets = (key) => {
    if (bulletGroup.length > 0) {
      elements.push(
        <ul key={`bullets-${key}`} className="ml-5 mb-3 space-y-1">
          {bulletGroup.map((b, i) => (
            <li key={i} className="text-gray-700 leading-relaxed list-disc">{b}</li>
          ))}
        </ul>
      );
      bulletGroup = [];
    }
  };

  lines.forEach((rawLine, idx) => {
    const line = rawLine.trim();
    if (!line) {
      if (prevWasBullet) {
        flushBullets(idx);
        prevWasBullet = false;
      }
      return;
    }

    // Detect day headers like "Friday • September 4" or "Saturday • September 5"
    if (/^(Sunday|Monday|Tuesday|Wednesday|Thursday|Friday|Saturday)\s*[•·\-]\s*/i.test(line)) {
      flushBullets(idx);
      prevWasBullet = false;
      elements.push(
        <h4 key={idx} className="text-base font-bold text-purple-700 mt-5 mb-2 pb-1 border-b border-purple-200">
          {line}
        </h4>
      );
      return;
    }

    // Detect time lines like "2:00–5:00 PM", "5:30 PM", "9:30 PM (Optional)"
    if (/^\d{1,2}:\d{2}\s*[–-]\s*\d{1,2}:\d{2}\s*(AM|PM)/i.test(line) ||
        /^\d{1,2}:\d{2}\s*(AM|PM)/i.test(line) ||
        /^\d{1,2}:\d{2}\s*(AM|PM)\s*\(/i.test(line)) {
      flushBullets(idx);
      prevWasBullet = false;
      elements.push(
        <div key={idx} className="flex items-center gap-2 mt-3 mb-1">
          <span className="inline-flex items-center justify-center bg-purple-100 text-purple-700 text-sm font-semibold px-2.5 py-1 rounded-md whitespace-nowrap">
            {line}
          </span>
        </div>
      );
      return;
    }

    // Detect section headers (short lines with no punctuation, or title-case lines)
    const isSectionHeader = (
      (line.length < 60 && !line.endsWith('.') && !line.endsWith(',') &&
       !line.includes('•') && !line.includes('·') &&
       (/^[A-Z][A-Za-z\s&'\/-]+$/.test(line) || /^Choose Your/.test(line) || /^Retreat/.test(line) || /^Contact/.test(line) || /^Optional/.test(line))) &&
      !/^\d/.test(line)
    );

    // Known section headers
    const knownHeaders = [
      'Who Should Attend?', 'Retreat Highlights', 'Contact Information',
      'Choose Your Adventure', 'Optional Sunday',
      'Shopping', 'Thrifting', 'Historic Sites', 'Outdoor Adventures',
      'Recreation', 'Theme: Walking With Purpose'
    ];
    const isKnownHeader = knownHeaders.some(h => line === h);

    // Also detect sub-section headers: short Title Case lines that match known activity categories
    const subHeaders = ['Shopping', 'Thrifting', 'Historic Sites', 'Outdoor Adventures', 'Recreation'];
    const isSubHeader = subHeaders.some(h => line === h);

    if (isKnownHeader || isSubHeader || (isSectionHeader && line.split(' ').length <= 5 && line.length > 3)) {
      flushBullets(idx);
      prevWasBullet = false;
      const isMainHeader = isKnownHeader && !isSubHeader;
      elements.push(
        <h4 key={idx} className={`text-base ${isMainHeader ? 'font-semibold text-gray-900' : 'font-medium text-purple-600'} mt-4 mb-2`}>
          {line}
        </h4>
      );
      return;
    }

    // Detect location lines starting with 📍
    if (line.startsWith('📍')) {
      flushBullets(idx);
      prevWasBullet = false;
      elements.push(
        <p key={idx} className="text-gray-600 text-sm mb-1 flex items-start gap-1">
          <span>{line}</span>
        </p>
      );
      return;
    }

    // Detect phone lines starting with 📞
    if (line.startsWith('📞')) {
      flushBullets(idx);
      prevWasBullet = false;
      elements.push(
        <p key={idx} className="text-gray-600 text-sm mb-1 flex items-start gap-1">
          <span>{line}</span>
        </p>
      );
      return;
    }

    // Detect calendar emoji lines
    if (line.startsWith('📅')) {
      flushBullets(idx);
      prevWasBullet = false;
      elements.push(
        <p key={idx} className="text-gray-600 text-sm mb-1">
          {line}
        </p>
      );
      return;
    }

    // Detect bullet items (lines that are short, no period, and look like list items)
    const isBullet = (
      line.length < 80 &&
      !line.endsWith('.') &&
      !line.endsWith('!') &&
      !line.endsWith('?') &&
      !line.includes(':') &&
      !/^\d{1,2}:\d{2}/.test(line) &&
      !line.startsWith('📍') &&
      !line.startsWith('📞') &&
      !line.startsWith('📅') &&
      !line.startsWith('Guests') &&
      !line.startsWith('Participants') &&
      !line.startsWith('Everyone') &&
      !line.startsWith('Choose') &&
      !line.startsWith('Whether') &&
      !line.startsWith('This retreat') &&
      !line.startsWith('Following') &&
      !line.startsWith('Hosted') &&
      !line.startsWith('The retreat') &&
      !line.startsWith('Relax') &&
      !line.startsWith('Enjoy') &&
      !line.startsWith('Celebrate') &&
      !line.startsWith('Suggested') &&
      !line.startsWith('Explore') &&
      !line.startsWith('Discover') &&
      !line.startsWith('Visit') &&
      !line.startsWith('Take') &&
      !line.startsWith('Guests may') &&
      line.split(' ').length <= 8
    );

    if (isBullet) {
      bulletGroup.push(line);
      prevWasBullet = true;
    } else {
      flushBullets(idx);
      prevWasBullet = false;
      elements.push(
        <p key={idx} className="text-gray-700 leading-relaxed mb-2">
          {line}
        </p>
      );
    }
  });

  flushBullets('end');

  return <div>{elements}</div>;
};

export default EventDetail;
